import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { alpha, styled } from '../styles';
export type VARIANT = 'default' | 'alternative' | 'brandedLight' | 'alternativeLight' | 'secondary' | 'marketing';
export type PADDING = 'default' | 'defaultBottom' | 'defaultTop' | 'none' | 'reduced';
export interface Props {
  children: ReactNode;
  variant?: VARIANT;
  paddingVariant?: PADDING;
  customColor?: string;
  id?: string;
  className?: string;
  dataTestId?: string;
  hasStyledBorders?: boolean;
}
const StyledSection = styled('section', {
  shouldForwardProp: prop => !(['variant', 'paddingVariant', 'customColor', 'hasStyledBorders'] as unknown[]).includes(prop)
})<Props>(({
  theme: {
    spacing,
    palette
  },
  variant,
  customColor,
  paddingVariant,
  hasStyledBorders
}) => {
  const getBackgroundColor = (bgVariant: VARIANT) => {
    if (customColor && variant === 'marketing') {
      return customColor;
    }
    const variantToColor = {
      alternative: palette.layout.background.alternative,
      default: 'transparent',
      brandedLight: alpha(palette.primary.main, 0.04),
      alternativeLight: '#FAFAFA',
      secondary: palette.text.onMainSecondary
    } as Record<VARIANT, string>;
    return variantToColor[bgVariant];
  };
  const getPadding = (padding: PADDING) => {
    const paddingToValue = {
      none: 0,
      reduced: spacing(4, 0),
      default: spacing(9, 0),
      defaultBottom: spacing(0, 0, 9, 0),
      defaultTop: spacing(9, 0, 0, 0)
    } as Record<PADDING, number | string>;
    return paddingToValue[padding];
  };
  const borderColor = customColor ?? palette.primary.main;
  return {
    backgroundColor: getBackgroundColor(variant as VARIANT),
    padding: getPadding(paddingVariant as PADDING),
    ...{
      ...(hasStyledBorders ? {
        position: 'relative',
        '&:before': {
          background: `linear-gradient(90deg, ${alpha(borderColor, 0.24)} 0%, ${borderColor} 100%)`,
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          height: 1,
          width: '50%'
        },
        '&:after': {
          background: `linear-gradient(90deg, ${borderColor} 0%, ${alpha(borderColor, 0.24)} 100%)`,
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: 1,
          width: '50%'
        }
      } : {})
    }
  };
});
const Section = t0 => {
  const $ = _c(12);
  let children;
  let dataTestId;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      dataTestId,
      children,
      variant: t1,
      paddingVariant: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = dataTestId;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    dataTestId = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const variant = t1 === undefined ? "default" : t1;
  const paddingVariant = t2 === undefined ? "default" : t2;
  let t3;
  if ($[6] !== children || $[7] !== dataTestId || $[8] !== paddingVariant || $[9] !== props || $[10] !== variant) {
    t3 = <StyledSection data-testid={dataTestId} variant={variant} paddingVariant={paddingVariant} {...props}>{children}</StyledSection>;
    $[6] = children;
    $[7] = dataTestId;
    $[8] = paddingVariant;
    $[9] = props;
    $[10] = variant;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export default Section;